import React, { useState, useRef, useEffect } from 'react';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import StatusPill from '../../../components/ui/StatusPill';
// Select removido do cabeçalho; seleção ocorre via badge de status
import Icon from '../../../components/AppIcon';
import InfoTooltip from '../../../components/ui/InfoTooltip';
import { useAuth } from '../../../contexts/AuthContext';

const PrinterCard = ({ printer, periodType, periodStart, periodEnd, usageSharePct = 0, onEdit, onDelete, onViewHistory, onStatusChange, onAddMaintenanceLog, onPeriodTypeChange }) => {
  const status = printer?.status || 'idle';
  const { userProfile } = useAuth();
  const thumb = Array.isArray(printer?.image_urls) && printer?.image_urls?.length > 0
    ? printer?.image_urls?.[0]
    : printer?.image;

  const utilization = Number(printer?.utilization || 0);
  const totalHours = Number(printer?.totalHours || 0);
  const jobs = Number(printer?.completedJobs || 0);
  const purchasePrice = Number((printer?.purchasePrice || '').toString().replace(/[^0-9.,-]/g, '').replace('.', '').replace(',', '.')) || 0;
  const maintenanceLogs = Array.isArray(printer?.maintenanceLogs) ? printer?.maintenanceLogs : [];

  // Período selecionado
  const startDate = periodStart ? new Date(periodStart) : null;
  const endDateRaw = periodEnd ? new Date(periodEnd) : null;
  const endDate = endDateRaw ? new Date(endDateRaw.getTime()) : null;
  if (endDate) endDate.setHours(23, 59, 59, 999);
  const periodActive = Boolean(startDate && endDate && !isNaN(startDate) && !isNaN(endDate));

  const parseDate = (val) => {
    if (!val) return null;
    const d = new Date(val);
    return isNaN(d) ? null : d;
  };
  const isWithinPeriod = (d) => {
    if (!periodActive) return true;
    if (!d) return false;
    return d >= startDate && d <= endDate;
  };

  const maintenanceLogsUsed = periodActive
    ? maintenanceLogs.filter((log) => isWithinPeriod(parseDate(log?.date)))
    : maintenanceLogs;
  const maintenanceCostTotal = maintenanceLogsUsed?.reduce((sum, log) => sum + (Number(log?.amount || 0)), 0);
  const daysDownTotal = maintenanceLogsUsed?.reduce((sum, log) => sum + (Number(log?.daysDown || 0)), 0);
  const lastMaintenanceReason = (() => {
    const source = (Array.isArray(maintenanceLogsUsed) && maintenanceLogsUsed.length > 0)
      ? maintenanceLogsUsed
      : maintenanceLogs;
    if (!Array.isArray(source) || source.length === 0) return null;
    const last = source[source.length - 1];
    return last?.reason || null;
  })();
  const lastMaintenanceAmount = (() => {
    const source = (Array.isArray(maintenanceLogsUsed) && maintenanceLogsUsed.length > 0)
      ? maintenanceLogsUsed
      : maintenanceLogs;
    if (!Array.isArray(source) || source.length === 0) return null;
    const last = source[source.length - 1];
    const amt = Number(last?.amount || 0);
    return amt > 0 ? amt : 0;
  })();
  // Fallback de 30 dias apenas quando não há período selecionado
  const daysDownLast30 = !periodActive ? maintenanceLogs?.reduce((sum, log) => {
    const d = parseDate(log?.date);
    if (!d) return sum;
    const threshold = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
    return d >= threshold ? sum + (Number(log?.daysDown || 0)) : sum;
  }, 0) : 0;
  // Percentual de dias em operação (desde compra, se disponível; caso contrário, nos últimos 30 dias)
  const daysSincePurchase = (() => {
    const d = new Date(printer?.purchaseDate);
    if (!isNaN(d)) {
      const diffMs = Date.now() - d.getTime();
      return Math.max(1, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
    }
    return null;
  })();
  const operationBasisDays = (() => {
    if (periodActive) {
      const msPerDay = 24 * 60 * 60 * 1000;
      const diffMs = endDate.getTime() - startDate.getTime();
      return Math.max(1, Math.ceil(diffMs / msPerDay) + 1);
    }
    return daysSincePurchase || 30;
  })();
  const operationDays = (() => {
    if (periodActive) {
      return Math.max(0, operationBasisDays - Number(daysDownTotal || 0));
    }
    if (daysSincePurchase) {
      return Math.max(0, daysSincePurchase - Number(daysDownTotal || 0));
    }
    const windowDays = 30;
    return Math.max(0, windowDays - Number(daysDownLast30 || 0));
  })();
  const operationPct = (() => {
    const basis = operationBasisDays || 1;
    return Math.max(0, Math.min(100, (operationDays / basis) * 100));
  })();
  const maintenanceDays = Math.max(0, operationBasisDays - operationDays);
  const activeDays = operationDays; // dias ativos
  const expenseTotal = purchasePrice + maintenanceCostTotal;
  const jobsData = Array.isArray(printer?.jobs) ? printer?.jobs : [];
  const getJobDate = (j) => {
    const d = parseDate(j?.completed_at) || parseDate(j?.created_at) || parseDate(j?.endTime) || parseDate(j?.startTime) || parseDate(j?.usage_date) || parseDate(j?.date);
    return d;
  };
  const jobsDataUsed = periodActive ? jobsData?.filter((j) => isWithinPeriod(getJobDate(j))) : jobsData;
  const jobHours = (j) => Number(j?.duration_hours || j?.estimated_duration_hours || j?.durationHours || j?.hours || 0);
  const totalHoursUsed = periodActive ? (jobsDataUsed?.reduce((sum, j) => sum + jobHours(j), 0) || 0) : totalHours;
  const revenueEstimated = 0; // sem fallback de taxa por hora
  const revenueRealAll = jobsData?.length > 0 
    ? jobsData?.reduce((sum, j) => sum + (Number(j?.price || j?.revenue || 0)), 0)
    : null;
  const revenueRealPeriod = jobsDataUsed?.length > 0 
    ? jobsDataUsed?.reduce((sum, j) => sum + (Number(j?.price || j?.revenue || 0)), 0)
    : null;
  const revenueToShowLifetime = revenueRealAll ?? 0;
  const revenueToShow = periodActive ? (revenueRealPeriod ?? 0) : revenueToShowLifetime;
  const breakEvenProgress = purchasePrice > 0 ? Math.max(0, Math.min(100, (revenueToShowLifetime / purchasePrice) * 100)) : 0;
  const isPaidOff = purchasePrice > 0 ? revenueToShowLifetime >= purchasePrice : false;

  // Métricas solicitadas: potência em kW, dias/mês, horas/dia, retorno em meses
  const powerKw = (() => {
    const w = Number((printer?.powerConsumption || '').toString().replace(/[^0-9.,-]/g, '').replace('.', '').replace(',', '.')) || 0;
    return w > 0 ? (w / 1000) : null;
  })();
  const daysPerMonth = Number(printer?.daysWorkedPerMonth || 0);
  const avgHoursPerDay = Number(printer?.avgHoursPerDay || 0);
  const paybackMonthsValue = (() => {
    const explicit = Number(printer?.paybackMonths || 0);
    return explicit > 0 ? explicit : null;
  })();

  // Estimativa de dias ociosos com base em utilização, para barra tricolor (depende de totalHoursUsed)
  const utilPctBase = Math.max(0, Math.min(100, Number(utilization || 0)));
  const utilPct = (() => {
    const targetHoursPerDay = Number(printer?.avgHoursPerDay || 0);
    if (periodActive && targetHoursPerDay > 0 && operationBasisDays > 0) {
      const maxHoursPeriod = targetHoursPerDay * operationBasisDays;
      if (maxHoursPeriod > 0) {
        return Math.max(0, Math.min(100, (totalHoursUsed / maxHoursPeriod) * 100));
      }
    }
    return utilPctBase;
  })();
  const activeDaysTri = Math.max(0, Math.min(operationBasisDays, Math.round((utilPct / 100) * operationBasisDays)));
  const maintenanceDaysTri = Math.max(0, Math.min(operationBasisDays - activeDaysTri, maintenanceDays));
  const idleDaysTri = Math.max(0, operationBasisDays - activeDaysTri - maintenanceDaysTri);
  const activePctTri = operationBasisDays > 0 ? (activeDaysTri / operationBasisDays) * 100 : 0;
  const maintPctTri = operationBasisDays > 0 ? (maintenanceDaysTri / operationBasisDays) * 100 : 0;
  const idlePctTri = operationBasisDays > 0 ? (idleDaysTri / operationBasisDays) * 100 : 0;

  // Kg de filamento impresso: tenta somar campos comuns nos jobs; fallback para printer.filamentPrintedKg
  const kilogramsPrintedAll = (() => {
    if (Array.isArray(jobsData) && jobsData?.length > 0) {
      const grams = jobsData?.reduce((sum, j) => sum + (Number(j?.weight_grams || j?.filamentWeightGrams || j?.filament_weight || 0)), 0);
      if (grams > 0) return grams / 1000;
    }
    return Number(printer?.filamentPrintedKg || 0);
  })();
  const kilogramsPrinted = (() => {
    if (Array.isArray(jobsDataUsed) && jobsDataUsed?.length > 0) {
      const grams = jobsDataUsed?.reduce((sum, j) => sum + (Number(j?.weight_grams || j?.filamentWeightGrams || j?.filament_weight || 0)), 0);
      if (grams > 0) return grams / 1000;
    }
    return periodActive ? 0 : kilogramsPrintedAll;
  })();

  const formatCurrency = (value) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

  // Formatação para cabeçalho do período (DD/MM/AAAA)
  const formatDateLong = (dateObj) => {
    if (!dateObj || isNaN(dateObj)) return null;
    const dd = String(dateObj.getDate()).padStart(2, '0');
    const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
    const yyyy = String(dateObj.getFullYear());
    return `${dd}/${mm}/${yyyy}`;
  };
  const periodHeaderLabel = periodActive ? `Período: ${formatDateLong(startDate)}–${formatDateLong(endDate)}` : 'Todo o Período';

  // Badge de volume de impressão
  const buildVolumeLabel = (() => {
    if (printer?.build_volume) return String(printer?.build_volume);
    const x = printer?.build_volume_x;
    const y = printer?.build_volume_y;
    const z = printer?.build_volume_z;
    if (x && y && z) return `${x}×${y}×${z}mm`;
    return null;
  })();

  // Média de filamento por job (kg)
  const avgFilamentPerJobKg = (() => {
    if (Array.isArray(jobsDataUsed) && jobsDataUsed?.length > 0) {
      const weights = jobsDataUsed?.map(j => Number(j?.weight_grams || j?.filamentWeightGrams || j?.filament_weight || 0)).filter(w => w > 0);
      const count = weights?.length || 0;
      const totalGrams = weights?.reduce((sum, w) => sum + w, 0);
      return count > 0 ? (totalGrams / 1000) / count : null;
    }
    return null;
  })();

  // Estimativa de custo total de filamento utilizado (R$)
  const avgFilamentCostPerKg = (() => {
    const v = Number(printer?.avgFilamentCostPerKg || printer?.filamentCostPerKg || 0);
    return v > 0 ? v : 100; // fallback razoável se não informado
  })();
  const filamentCostTotal = kilogramsPrinted > 0 ? (kilogramsPrinted * avgFilamentCostPerKg) : 0;

  // Estimativa de custo total de eletricidade (R$)
  const electricityRate = (() => {
    const raw = (userProfile?.electricityRate ?? printer?.electricityRate ?? 0.75);
    const cleaned = typeof raw === 'string'
      ? raw.toString().replace(/[^0-9.,-]/g, '').replace('.', '').replace(',', '.')
      : raw;
    const n = Number(cleaned);
    return (n && n > 0) ? n : 0.75;
  })();
  const electricityCostTotal = (powerKw || 0) * totalHoursUsed * electricityRate;

  // Lucro gerado estimado (receita - custos diretos estimados)
  const jobsCount = Array.isArray(jobsDataUsed) && jobsDataUsed?.length > 0 ? jobsDataUsed?.length : (periodActive ? 0 : (Number(printer?.completedJobs || 0) || 0));
  const profitGenerated = (revenueToShow || 0) - (filamentCostTotal || 0) - (electricityCostTotal || 0);
  const avgProfitPerJob = jobsCount > 0 ? (profitGenerated / jobsCount) : null;

  // % de falhas de impressão
  const failureRatePct = (() => {
    if (Array.isArray(jobsDataUsed) && jobsDataUsed?.length > 0) {
      const total = jobsDataUsed?.length;
      const failed = jobsDataUsed?.filter(j => {
        const s = (j?.status || '').toString().toLowerCase();
        const f = Boolean(j?.failed || j?.is_failed);
        return s === 'failed' || f === true;
      })?.length || 0;
      return total > 0 ? (failed / total) * 100 : null;
    }
    return null;
  })();

  // Média de filamento por job (R$)
  const avgFilamentPerJobBRL = avgFilamentPerJobKg != null ? (avgFilamentPerJobKg * avgFilamentCostPerKg) : null;

  // Desperdício de material (kg e R$)
  const wasteKg = (() => {
    if (Array.isArray(jobsDataUsed) && jobsDataUsed?.length > 0) {
      const grams = jobsDataUsed?.reduce((sum, j) => sum
        + (Number(j?.waste_grams || j?.material_waste_grams || 0))
        + (Number(j?.support_grams || j?.supports_weight_grams || j?.support_weight_grams || 0))
        + (Number(j?.failed_weight_grams || 0))
      , 0);
      if (grams > 0) return grams / 1000;
    }
    const pct = Number(printer?.materialWastePct || printer?.wastePercentage || 0);
    const fallbackPct = pct > 0 ? pct : 7; // estimativa padrão se não houver dados
    return kilogramsPrinted > 0 ? (kilogramsPrinted * (fallbackPct / 100)) : 0;
  })();
  const wasteBRL = wasteKg > 0 ? (wasteKg * avgFilamentCostPerKg) : 0;
  const grossProfit = (revenueToShow || 0) - (filamentCostTotal || 0);
  const profitAfterMaintenance = grossProfit - (maintenanceCostTotal || 0);
  const netProfit = (revenueToShow || 0) - (filamentCostTotal || 0) - (electricityCostTotal || 0) - (maintenanceCostTotal || 0) - (wasteBRL || 0);
  const formatDateBR = (dateStr) => {
    if (!dateStr) return '—';
    try {
      // aceita 'YYYY-MM-DD' ou outras; tenta Date
      const d = new Date(dateStr);
      if (!isNaN(d)) {
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = String(d.getFullYear());
        return `${dd}/${mm}/${yyyy}`;
      }
      // fallback para strings já formatadas
      const m = dateStr.match(/^([0-9]{4})-([0-9]{2})-([0-9]{2})$/);
      if (m) return `${m[3]}/${m[2]}/${m[1]}`;
      return dateStr;
    } catch {
      return dateStr;
    }
  };

  // Dropdown de período no cabeçalho
  const periodOptions = [
    { value: 'current_month', label: 'Mês atual' },
    { value: 'previous_month', label: 'Mês anterior' },
    { value: 'last_30d', label: 'Últimos 30 dias' },
    { value: 'last_90d', label: 'Últimos 90 dias' },
    { value: 'all_time', label: 'Todo o Período' },
    { value: 'custom', label: 'Personalizado' }
  ];

  const periodBtnRef = useRef(null);
  const periodMenuRef = useRef(null);
  const [showPeriodMenu, setShowPeriodMenu] = useState(false);

  useEffect(() => {
    const handleDocumentClick = (e) => {
      if (!showPeriodMenu) return;
      const target = e?.target;
      const insideBtn = periodBtnRef?.current && periodBtnRef.current.contains(target);
      const insideMenu = periodMenuRef?.current && periodMenuRef.current.contains(target);
      if (!insideBtn && !insideMenu) {
        setShowPeriodMenu(false);
      }
    };
    const handleKeyDown = (e) => {
      if (!showPeriodMenu) return;
      if (e.key === 'Escape') setShowPeriodMenu(false);
    };
    document.addEventListener('mousedown', handleDocumentClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showPeriodMenu]);

  const statusOptions = [
    { value: 'active', label: 'Ativa' },
    { value: 'idle', label: 'Ociosa' },
    { value: 'maintenance', label: 'Em manutenção' },
    { value: 'inactive', label: 'Fora de serviço' },
    { value: 'nonexistent', label: 'Inexistente (vendida/retirada)' }
  ];
  const [showStatusMenu, setShowStatusMenu] = useState(false);

  const handleStatusSelect = (newStatus) => {
    if (!newStatus) return;
    if (newStatus === 'maintenance') {
      // abre modal para registrar manutenção
      onStatusChange && onStatusChange(printer, newStatus);
      onAddMaintenanceLog && onAddMaintenanceLog(printer);
      return;
    }
    onStatusChange && onStatusChange(printer, newStatus);
  };

  return (
    <section className="relative rounded-2xl bg-card border border-border shadow-none overflow-hidden transition-all hover:shadow-xs">
      {/* Badges à direita: marca e status atual */}
      <div className="absolute top-4 right-4 flex flex-col items-end gap-1 z-10">
        {(printer?.brand || printer?.manufacturer) && (
          <span
            className="text-xs px-3 py-1 rounded-full bg-black/50 text-white border border-white/30 shadow-xs backdrop-blur-sm"
            title={printer?.brand || printer?.manufacturer}
          >
            {printer?.brand || printer?.manufacturer}
          </span>
        )}
        {/* Badge de status clicável para abrir seleção */}
        <button
          type="button"
          className="text-[11px] rounded-full focus:outline-none focus:ring-2 focus:ring-primary/40"
          title="Clique para alterar status"
          onClick={() => setShowStatusMenu(v => !v)}
        >
          <span className="inline-flex rounded-full bg-black/30 border border-white/20 p-0.5 backdrop-blur-sm">
            <StatusPill status={status} className="" />
          </span>
        </button>
        {showStatusMenu && (
          <div className="mt-1 w-44 rounded-xl border border-border bg-card shadow-xs overflow-hidden">
            {statusOptions.map(opt => (
              <button
                key={opt.value}
                type="button"
                className={`w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2 ${status === opt.value ? 'text-primary' : 'text-foreground'}`}
                onClick={() => {
                  setShowStatusMenu(false);
                  handleStatusSelect(opt.value);
                }}
              >
                <Icon name={opt.value === 'maintenance' ? 'Wrench' : opt.value === 'active' ? 'CheckCircle' : opt.value === 'idle' ? 'PauseCircle' : opt.value === 'inactive' ? 'XCircle' : 'Archive'} size={16} />
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Foto grande ocupando a largura inteira */}
      <div className="w-full h-40 sm:h-48 md:h-52 bg-muted overflow-hidden">
        <Image src={thumb} alt={printer?.imageAlt || printer?.name} className="w-full h-full object-cover" />
      </div>

      {/* Badges de AMS e Bico logo abaixo da imagem */}
      <div className="px-5 pt-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="px-2.5 py-1 text-xs rounded-md bg-muted/40 border border-border/60 text-foreground" title="Sistema de gerenciamento automático de materiais">
            AMS: {printer?.hasAMS ? 'Sim' : (printer?.hasAMS === false ? 'Não' : '—')}
          </span>
          <span className="px-2.5 py-1 text-xs rounded-md bg-muted/40 border border-border/60 text-foreground" title="Diâmetro do bico instalado">
            Bico: {printer?.nozzleSize ? `${printer?.nozzleSize} mm` : '—'}
          </span>
          {buildVolumeLabel && (
            <span className="px-2.5 py-1 text-xs rounded-md bg-muted/40 border border-border/60 text-foreground" title="Volume de impressão">
              Volume: {buildVolumeLabel}
            </span>
          )}
        </div>
      </div>

      {/* Cabeçalho simples abaixo da foto */}
      <header className="px-5 pt-4 pb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground" title={printer?.model || 'Modelo não informado'}>{printer?.model || 'Modelo não informado'}</span>
          </div>
          <h3 className="text-base font-medium text-foreground mt-1" title={printer?.name || printer?.model || 'Impressora'}>
            {printer?.name || 'Impressora'}
          </h3>
          {periodHeaderLabel && (
            <div className="relative inline-block">
              <button
                ref={periodBtnRef}
                type="button"
                className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-xs hover:bg-muted/80"
                title="Alterar período"
                onClick={() => setShowPeriodMenu(v => !v)}
                aria-haspopup="menu"
                aria-expanded={showPeriodMenu}
              >
                <Icon name="Calendar" size={12} />
                <span className="truncate max-w-[240px]">{periodHeaderLabel}</span>
                <Icon name="ChevronDown" size={12} className={showPeriodMenu ? 'rotate-180 transition-transform' : 'transition-transform'} />
              </button>

              {showPeriodMenu && (
                <div
                  ref={periodMenuRef}
                  className="absolute z-50 mt-1 left-0 bg-white text-black border border-border rounded-md shadow-md min-w-[200px]"
                  role="menu"
                >
                  {periodOptions.map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      className={`relative flex w-full items-center justify-between px-3 py-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground ${periodType === opt.value ? 'bg-primary text-primary-foreground' : ''}`}
                      onClick={() => {
                        if (typeof onPeriodTypeChange === 'function') onPeriodTypeChange(opt.value);
                        setShowPeriodMenu(false);
                      }}
                      role="menuitem"
                    >
                      <span>{opt.label}</span>
                      {periodType === opt.value && <Icon name="Check" size={14} />} 
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </header>

      {/* Content */}
        <div className="px-5 pb-5">
          {/* Barra de uso relativa ao conjunto (não exibe para 'Inexistente') */}
          {status !== 'nonexistent' && (() => {
            const pctRaw = Number(usageSharePct || 0);
            const pct = Math.max(0, Math.min(100, pctRaw));
            const label = pct <= 33 ? 'Uso baixo no conjunto' : pct <= 66 ? 'Uso médio no conjunto' : 'Uso alto no conjunto';
            const colorCls = pct <= 33 ? 'text-success' : pct <= 66 ? 'text-warning' : 'text-error';
            const barCls = pct <= 33 ? 'bg-success' : pct <= 66 ? 'bg-warning' : 'bg-error';
            return (
              <div className="mb-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-muted-foreground">Uso relativo</span>
                  <span className="text-sm font-medium text-foreground">{totalHoursUsed}h</span>
                </div>
                <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                  <div className={`${barCls} h-2 rounded-full transition-all`} style={{ width: `${Math.max(pct, 5)}%` }} />
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className={`${colorCls} text-xs font-medium`}>{label}</span>
                  <span className="text-xs text-muted-foreground">{pct.toFixed(1)}%</span>
                </div>
              </div>
            );
          })()}

          {/* Barra de Break-even */}
          {(() => {
            const pct = Math.max(0, Math.min(100, breakEvenProgress));
            return (
              <div className="mb-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-muted-foreground">Break-even</span>
                  <span className="text-sm font-medium text-foreground">{pct.toFixed(0)}%</span>
                </div>
                <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                  <div className={`bg-primary h-2 rounded-full transition-all`} style={{ width: `${Math.max(pct, 3)}%` }} />
                </div>
                <div className="text-xs text-muted-foreground mt-2">
                  {isPaidOff ? 'Pago' : 'Ainda não se pagou'}
                </div>
              </div>
            );
          })()}

          {/* Barra Tricolor: Ativos / Manutenção / Ociosos */}
          {(() => {
            return (
              <div className="mb-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-muted-foreground">Dias (ativos / manutenção / ociosos)</span>
                  <span className="text-sm font-medium text-foreground">{operationBasisDays} dias</span>
                </div>
                <div className="h-2 w-full rounded-full bg-muted overflow-hidden flex">
                  <div className={`bg-success h-2`} style={{ width: `${Math.max(activePctTri, 0)}%` }} />
                  <div className={`bg-warning h-2`} style={{ width: `${Math.max(maintPctTri, 0)}%` }} />
                  <div className={`bg-accent h-2`} style={{ width: `${Math.max(idlePctTri, 0)}%` }} />
                </div>
                <div className="text-xs text-muted-foreground mt-2 flex flex-wrap gap-3">
                  <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-success" /> {activeDaysTri} ativos ({activePctTri.toFixed(0)}%)</span>
                  <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-warning" /> {maintenanceDaysTri} manutenção ({maintPctTri.toFixed(0)}%)</span>
                  <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-accent" /> {idleDaysTri} ociosos ({idlePctTri.toFixed(0)}%)</span>
                </div>
              </div>
            );
          })()}

          {/* Divider */}
          <div className="h-px w-full bg-muted my-4" />

          {/* Operação: dias/mês, horas/dia, potência kW, dias em operação, filamento impresso */}
          <div className="mt-6">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="Activity" size={16} className="text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">Operação</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div className="p-3 rounded-lg bg-muted/30 min-w-0">
                <div className="flex items-center space-x-2 text-xs text-muted-foreground mb-1">
                  <Icon name="Calendar" size={14} />
                  <span>Dias em operação (período)</span>
                </div>
                <div className="text-sm font-semibold text-foreground">{operationDays > 0 ? operationDays : '—'}</div>
              </div>
              <div className="p-3 rounded-lg bg-muted/30 min-w-0">
                <div className="flex items-center space-x-2 text-xs text-muted-foreground mb-1">
                  <Icon name="Clock" size={14} />
                  <span>Horas/dia (período)</span>
                </div>
                <div className="text-sm font-semibold text-foreground">{operationDays > 0 ? Number(totalHoursUsed / operationDays).toFixed(1) : '—'}</div>
              </div>
              <div className="p-3 rounded-lg bg-muted/30 min-w-0">
                <div className="flex items-center space-x-2 text-xs text-muted-foreground mb-1">
                  <Icon name="Zap" size={14} />
                  <span>Potência</span>
                </div>
                <div className="text-sm font-semibold text-foreground">{powerKw != null ? `${powerKw.toFixed(2)} kW` : '—'}</div>
              </div>
              {/* Novo: Jobs realizados (período ou vida toda) */}
              <div className="p-3 rounded-lg bg-muted/30 min-w-0">
                <div className="flex items-center space-x-2 text-xs text-muted-foreground mb-1">
                  <Icon name="ListChecks" size={14} />
                  <span>Jobs realizados</span>
                </div>
                <div className="text-sm font-semibold text-foreground">{jobsCount > 0 ? jobsCount : '—'}</div>
              </div>
            </div>
            {/* Linhas adicionais de operação na ordem solicitada */}
            <div className="mt-3 space-y-2">
              <div className="flex items-center justify-between" title="Soma dos pesos dos jobs convertidos para kg">
                <span className="text-sm text-muted-foreground">Filamento Impresso Total (Kg)</span>
                <span className="text-sm font-semibold text-foreground">{kilogramsPrinted > 0 ? `${kilogramsPrinted?.toFixed(2)} kg` : '—'}</span>
              </div>
              <div className="flex items-center justify-between" title="Total de material impresso multiplicado pelo custo médio por kg (avgFilamentCostPerKg)">
                <span className="text-sm text-muted-foreground">Filamento Impresso Total (R$)</span>
                <span className="text-sm font-semibold text-foreground">{filamentCostTotal > 0 ? formatCurrency(filamentCostTotal) : '—'}</span>
              </div>
              <div className="h-px w-full bg-muted my-2" />
              <div className="flex items-center justify-between" title="Média dos pesos dos jobs com peso informado, convertido para kg">
                <span className="text-sm text-muted-foreground">Média de filamento por job (Kg)</span>
                <span className="text-sm font-semibold text-foreground">{avgFilamentPerJobKg != null && avgFilamentPerJobKg > 0 ? `${avgFilamentPerJobKg?.toFixed(3)} kg` : '—'}</span>
              </div>
              <div className="flex items-center justify-between" title="Média (kg) por job multiplicada pelo custo médio por kg">
                <span className="text-sm text-muted-foreground">Média de filamento por job (R$)</span>
                <span className="text-sm font-semibold text-foreground">{avgFilamentPerJobBRL != null ? formatCurrency(avgFilamentPerJobBRL) : '—'}</span>
              </div>
              <div className="h-px w-full bg-muted my-2" />
              <div className="flex items-center justify-between" title="Percentual de jobs com status 'failed' ou flag de falha">
                <span className="text-sm text-muted-foreground">% de falhas em impressão</span>
                <span className="text-sm font-semibold text-foreground">{failureRatePct != null ? `${failureRatePct?.toFixed(1)}%` : '—'}</span>
              </div>
              <div className="flex items-center justify-between" title="Soma dos pesos descartados (waste/support/failed_weight) em kg; se ausente, estimativa pela taxa de desperdício">
                <span className="text-sm text-muted-foreground">Desperdício de material (Kg)</span>
                <span className="text-sm font-semibold text-foreground">{wasteKg > 0 ? `${wasteKg?.toFixed(3)} kg` : '—'}</span>
              </div>
              <div className="flex items-center justify-between" title="Desperdício (kg) multiplicado pelo custo médio por kg">
                <span className="text-sm text-muted-foreground">Desperdício de material (R$)</span>
                <span className="text-sm font-semibold text-foreground">{wasteBRL > 0 ? formatCurrency(wasteBRL) : '—'}</span>
              </div>
              <div className="h-px w-full bg-muted my-2" />
            </div>
          </div>

          {/* Divider removido para evitar duplicidade acima de Financeiro */}

          {/* Financeiro: linhas em ordem solicitada */}
          <div className="mt-6">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="TrendingUp" size={16} className="text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">Financeiro</span>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground inline-flex items-center">
                  Lucro Bruto
                  <InfoTooltip content="Lucro Bruto = Receita − Custo do Filamento" />
                </span>
                <span className="text-sm font-semibold text-foreground">{revenueToShow != null ? formatCurrency(grossProfit) : '—'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground inline-flex items-center">
                  Lucro Líquido
                  <InfoTooltip content="Lucro Líquido = Receita − Filamento − Energia − Manutenção − Desperdício" />
                </span>
                <span className="text-sm font-semibold text-foreground">{revenueToShow != null ? formatCurrency(netProfit) : '—'}</span>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="h-px w-full bg-muted my-4" />

          {/* Manutenção: linhas com valor, dias e último motivo */}
          <div className="mt-6">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="Wrench" size={16} className="text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">Manutenção</span>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground inline-flex items-center">
                  Total em Manutenção
                  <InfoTooltip content="Soma dos valores de manutenção no período" />
                </span>
                <span className="text-sm font-semibold text-foreground">{formatCurrency(maintenanceCostTotal)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground inline-flex items-center">
                  Valor da Última Manutenção
                  <InfoTooltip content="Valor do último registro no período" />
                </span>
                <span className="text-sm font-semibold text-foreground">{lastMaintenanceAmount > 0 ? formatCurrency(lastMaintenanceAmount) : '—'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground inline-flex items-center">
                  Dias em manutenção
                  <InfoTooltip content="Soma dos dias em manutenção no período" />
                </span>
                <span className="text-sm font-semibold text-foreground">{Number(daysDownTotal || 0)} dias</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground inline-flex items-center">
                  Último motivo
                  <InfoTooltip content="Motivo do último registro de manutenção no período" />
                </span>
                <span className="text-sm font-semibold text-foreground truncate max-w-[60%]">{lastMaintenanceReason || '—'}</span>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="h-px w-full bg-muted my-4" />

          {/* Detalhes adicionais */}
          <div className="mt-6 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Data Aquisição</span>
              <span className="text-sm font-medium text-foreground">{formatDateBR(printer?.purchaseDate)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Preço pago</span>
              <span className="text-sm font-medium text-foreground">{purchasePrice > 0 ? formatCurrency(purchasePrice) : '—'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Fornecedor</span>
              <span className="text-sm font-medium text-foreground truncate max-w-[60%]" title={printer?.supplier || '—'}>{printer?.supplier || '—'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Última atividade</span>
              <span className="text-xs font-medium text-foreground" title={printer?.lastActivity}>{printer?.lastActivity || 'Sem registros'}</span>
            </div>
          </div>

          {/* Removido: badges movidos para o topo */}
        </div>

        {/* Rodapé: toolbar contínua de ações (sem dropdown) */}
        <div className="px-5 pb-5 pt-6 border-t border-border">
          <div className="flex rounded-lg overflow-hidden divide-x divide-border border border-border/70 bg-muted/30">
            <Button variant="ghost" size="xs" iconSize={16} className="flex-1 rounded-none" onClick={() => onEdit?.(printer)} iconName="Edit" iconPosition="left">Editar</Button>
            <Button variant="ghost" size="xs" iconSize={16} className="flex-1 rounded-none" onClick={() => onViewHistory?.(printer)} iconName="Projects" iconPosition="left">Histórico</Button>
            <Button variant="ghost" size="xs" iconSize={16} className="flex-1 rounded-none text-error" onClick={() => onDelete?.(printer)} iconName="Trash2" iconPosition="left">Excluir</Button>
          </div>
        </div>
    </section>
  );
};

export default PrinterCard;
